import { KEY_RECOIL } from '@/shared/config';
import ButtonTypes from '@/widgets/beats/ui/buttonTypes';
import EventSelectedInfo from '@/features/edit-event-strip';
import ArtifactAreasThumbnail from './artifactAreasThumbnail';
import EventEcgViewerWrapper from './eventEcgViewerWrapper';
import HrChart from './hrChart';
import StripEvents from './stripEvents';
import useArtifactAreas from './useArtifactAreas';

type ArtifactAreasProps = {
  isEmptyData?: boolean;
};

/**
 * Renders the artifact review area of the beats tab, pairing the thumbnail overview with the strip list, selected-event info, ECG viewer and heart-rate chart. Pulls study, profile, report window, timezone offset, ECG data map, chart ref and the arrow-navigation handler from useArtifactAreas, and short-circuits to a no-data message when the isEmptyData prop is set.
 *
 * @param props - Artifact area inputs, of which isEmptyData decides whether the strip content or the no-data message is shown.
 * @returns The artifact area layout, or a no-data placeholder when there are no artifact events.
 */
const ArtifactAreas = ({ isEmptyData }: ArtifactAreasProps) => {
  const {
    profileId,
    studyId,
    startReport,
    stopReport,
    timezoneOffset,
    ecgDataMap,
    hrChartRef,
    handleClickArrowButton,
  } = useArtifactAreas();

  return (
    <>
      <ArtifactAreasThumbnail
        timezoneOffset={timezoneOffset}
        profileId={profileId}
        studyId={studyId}
        startReport={startReport}
        stopReport={stopReport}
      />

      <div className="beat-type-strip-content">
        {isEmptyData ? (
          <div className="no-data-artifact">There is no data</div>
        ) : (
          <>
            <ButtonTypes />
            <StripEvents
              ecgDataMap={ecgDataMap}
              profileId={profileId}
              studyId={studyId}
              timezoneOffset={timezoneOffset}
              startReport={startReport}
            />
            <div className="beat-type-strip-content-container --min-width-strip-event">
              <div className="event-select-ecg-viewer-wrapper">
                <EventSelectedInfo
                  keyRecoil={KEY_RECOIL.BEAT_HR}
                  componentId="artifact-event-selected-info"
                  timezoneOffset={timezoneOffset}
                  ecgDataMap={ecgDataMap}
                />
                <EventEcgViewerWrapper
                  ecgDataMap={ecgDataMap}
                  profileId={profileId}
                  studyId={studyId}
                  timezoneOffset={timezoneOffset}
                  handleClickArrowButton={handleClickArrowButton}
                />
              </div>
              <HrChart
                ref={hrChartRef}
                ecgDataMap={ecgDataMap}
                timezoneOffset={timezoneOffset}
                studyId={studyId}
                profileId={profileId}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ArtifactAreas;
